'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyBottomBar from '@/components/StickyBottomBar';
import { createClient } from '@/lib/supabase/client';
import { Search, CheckCircle, ChevronRight, Shield, ArrowUpDown, AlertCircle } from 'lucide-react';

const CATEGORIES = [
  'All Categories',
  'Metabolic Research',
  'Recovery & Healing',
  'Growth Factor',
  'Cosmetic & Longevity',
  'Cognitive Research',
  'Compounding',
];

const REGIONS = [
  'All Regions',
  'Northeast',
  'Southeast',
  'Midwest',
  'West',
  'Southwest',
  'International',
];

type SortMode = 'most-verified' | 'newest' | 'alphabetical';

interface DbSupplier {
  id: string;
  name: string;
  logo_url: string;
  categories: string[];
  coa_verified: boolean;
  region: string;
  description: string;
  contact_email: string;
  facility_type: string;
  testing_partners: string[];
  founded_year: number;
  profile_image_url: string;
  compliance_notes: string;
}

export default function SuppliersDirectoryContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [region, setRegion] = useState('All Regions');
  const [coaOnly, setCoaOnly] = useState(false);
  const [sort, setSort] = useState<SortMode>('most-verified');
  const [suppliers, setSuppliers] = useState<DbSupplier[]>([]);
  const [totalVerified, setTotalVerified] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const supabase = createClient();
      let query = supabase
        .from('suppliers')
        .select('id, name, logo_url, categories, coa_verified, region, description, contact_email, facility_type, testing_partners, founded_year, profile_image_url, compliance_notes');

      // Category filter (array contains)
      if (category !== 'All Categories') {
        query = query.contains('categories', [category]);
      }

      // Region filter
      if (region !== 'All Regions') {
        query = query.eq('region', region);
      }

      // COA verified filter
      if (coaOnly) {
        query = query.eq('coa_verified', true);
      }

      // Search filter
      if (searchQuery.trim()) {
        query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      // Sort
      if (sort === 'most-verified') {
        query = query.order('coa_verified', { ascending: false }).order('name', { ascending: true });
      } else if (sort === 'newest') {
        query = query.order('founded_year', { ascending: false });
      } else {
        query = query.order('name', { ascending: true });
      }

      const { data, error: dbError } = await query;
      if (dbError) throw dbError;

      setSuppliers(data || []);

      // Get totals for hero stat (unfiltered)
      const { data: allData } = await supabase
        .from('suppliers')
        .select('id, coa_verified');
      const all = allData || [];
      setTotalCount(all.length);
      setTotalVerified(all.filter((s) => s.coa_verified).length);
    } catch (err: unknown) {
      setError('Failed to load suppliers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [category, region, coaOnly, sort, searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuppliers();
    }, searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchSuppliers, searchQuery]);

  return (
    <>
      <Header />
      <main className="pb-20">
        {/* Hero Banner */}
        <div className="relative h-[320px] lg:h-[380px] overflow-hidden">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1f46cc1d7-1786579932639.png"
            alt="Pharmaceutical laboratory environment representing verified peptide supplier facilities"
            fill
            priority
            sizes="100vw"
            className="object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pb-8 w-full">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={16} className="text-primary" />
                <span className="section-eyebrow text-white/80">Supplier Intelligence</span>
              </div>
              <h1 className="text-hero-md font-800 text-white leading-tight">
                Verified Supplier Directory
              </h1>
              <p className="mt-2 text-white/80 text-base">
                {totalVerified} of {totalCount} tracked suppliers now provide 3rd-party COA documentation
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          {/* Filters */}
          <div className="py-6 border-b border-border">
            <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-0 max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-base pl-9"
                />
              </div>

              {/* Category */}
              <div className="relative">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="select-base pr-8 min-w-[200px]"
                  aria-label="Filter by category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={`cat-opt-${c}`} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Region */}
              <div className="relative">
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className="select-base pr-8 min-w-[160px]"
                  aria-label="Filter by region"
                >
                  {REGIONS.map((r) => (
                    <option key={`reg-opt-${r}`} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* COA Toggle */}
              <label className="flex items-center gap-2.5 cursor-pointer flex-shrink-0">
                <div
                  onClick={() => setCoaOnly(!coaOnly)}
                  className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${coaOnly ? 'bg-primary' : 'bg-gray-300'}`}
                  role="switch"
                  aria-checked={coaOnly}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setCoaOnly(!coaOnly)}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${coaOnly ? 'translate-x-5' : 'translate-x-0.5'}`}
                  />
                </div>
                <span className="text-sm font-600 text-foreground whitespace-nowrap">COA Verified Only</span>
              </label>

              {/* Sort */}
              <div className="flex items-center gap-2 ml-auto">
                <ArrowUpDown size={14} className="text-muted-foreground" />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortMode)}
                  className="select-base pr-8 text-sm min-w-[160px]"
                  aria-label="Sort suppliers"
                >
                  <option value="most-verified">Most Verified</option>
                  <option value="newest">Newest</option>
                  <option value="alphabetical">Alphabetical</option>
                </select>
              </div>
            </div>

            {/* Active filter chips */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {loading ? (
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full" />
                  Loading suppliers...
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">
                  Showing <strong className="text-foreground">{suppliers.length}</strong> supplier{suppliers.length !== 1 ? 's' : ''}
                </span>
              )}
              {coaOnly && (
                <span className="verified-badge text-xs font-600 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle size={12} /> COA Verified Only
                </span>
              )}
              {category !== 'All Categories' && (
                <span className="text-xs font-600 px-2.5 py-0.5 rounded-full bg-muted text-foreground">
                  {category}
                </span>
              )}
              {region !== 'All Regions' && (
                <span className="text-xs font-600 px-2.5 py-0.5 rounded-full bg-muted text-foreground">
                  {region}
                </span>
              )}
            </div>
          </div>

          {/* Error */}
          {error && !loading && (
            <div className="py-10 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Supplier Grid */}
          {!loading && !error && suppliers.length === 0 ? (
            <div className="py-20 text-center">
              <Shield size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-700 text-foreground mb-2">No suppliers found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your filters or search query.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setCategory('All Categories');
                  setRegion('All Regions');
                  setCoaOnly(false);
                }}
                className="btn-primary"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-5 py-8">
              {suppliers.map((supplier) => (
                <SupplierCard key={supplier.id} supplier={supplier} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <StickyBottomBar />
    </>
  );
}

function SupplierCard({ supplier }: { supplier: DbSupplier }) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden card-hover flex flex-col">
      {/* Logo area */}
      <div className="relative h-32 bg-secondary overflow-hidden">
        <AppImage
          src={supplier.logo_url || 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&q=80'}
          alt={`${supplier.name} company logo and facility image`}
          fill
          sizes="400px"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-foreground/20" />
        {supplier.coa_verified ? (
          <div className="absolute top-3 right-3">
            <span className="verified-badge text-xs font-700 px-2 py-1 rounded-full flex items-center gap-1">
              <CheckCircle size={12} /> COA Verified
            </span>
          </div>
        ) : (
          <div className="absolute top-3 right-3">
            <span className="text-xs font-700 px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Verification Pending
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="mb-2">
          <h3 className="text-base font-700 text-foreground">{supplier.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{supplier.region} · Est. {supplier.founded_year}</p>
        </div>

        {/* Category tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(supplier.categories || []).map((cat) => (
            <span
              key={`${supplier.id}-cat-${cat}`}
              className="text-xs font-600 px-2 py-0.5 rounded bg-muted text-muted-foreground"
            >
              {cat}
            </span>
          ))}
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed flex-1 line-clamp-3">
          {supplier.description}
        </p>

        <Link
          href={`/supplier-profile-page?id=${supplier.id}`}
          className="mt-4 flex items-center justify-between text-sm font-700 text-primary hover:text-primary-dark transition-colors group"
        >
          View Profile
          <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
}
